import * as React from "react";
import { Avatar, Box, Button, Grid, Chip, Tooltip, Divider, IconButton, Fade, Typography } from "@mui/material";
import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  OndemandVideo as OndemandVideoIcon,
  AccountCircle as AccountCircleIcon,
  Groups as GroupsIcon,
  Apartment as ApartmentIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
} from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import { useAlert } from "../contexts/AlertProvider";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";
import NotificationBell from "./Notification/NotificationBell";
import GlobalSearch from "./GlobalSearch";
import { useTokens } from "../theme";
import { OPEN_SEARCH_EVENT } from "./KeyboardShortcuts";

const ROLE_LABELS = [
  ["ADMIN", "Admin"],
  ["GROUP_ADMIN", "Group Admin"],
  ["SALES_MANAGER", "Sales Manager"],
  ["GROUP_MANAGER", "Group Manager"],
];

const roleLabelFor = (roles = []) =>
  ROLE_LABELS.find(([role]) => roles.includes(role))?.[1] || "Agent";

const urlToPage = {
  adminTable: "Management Table",
  tutorials: "Video Tutorials",
  settings: "Settings",
};

export default function ToolBarNavigation({ collapsed = false, onToggleCollapse }) {
  const tokens = useTokens();
  const navigate = useNavigate();
  const { id, roles, resetUser, firstName: myFirstName, lastName: myLastName } = useUser();
  const { alert } = useAlert();
  const [searchParams] = useSearchParams();
  const [displayName, setDisplayName] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [searchOpen, setSearchOpen] = React.useState(false);

  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const currentPage = pathSegments[0];

  const paramId = searchParams.get("id");

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/agents/details/${paramId || id}/v1`
        );
        const result = await response.json();
        const { firstName, lastName } = result;
        setDisplayName(`${firstName} ${lastName.charAt(0)}.`);
        setFullName(`${firstName} ${lastName}`);
      } catch (error) {
        alert({
          title: "Error Occurred",
          detail: "Unable to retrieve agent data at the moment",
          severity: "error",
          duration: 5000,
        });
        console.error(error);
      }
    };

    fetchData();
  }, [paramId, id, alert]);

  React.useEffect(() => {
    const open = () => setSearchOpen(true);
    window.addEventListener(OPEN_SEARCH_EVENT, open);
    return () => window.removeEventListener(OPEN_SEARCH_EVENT, open);
  }, []);

  const toggleButton = (
    <Tooltip title={collapsed ? "Expand navigation" : "Collapse navigation"}>
      <IconButton
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        aria-expanded={!collapsed}
        onClick={onToggleCollapse}
        sx={{
          color: tokens.sidebarForeground,
          "&:hover": { bgcolor: tokens.sidebarHover, color: tokens.sidebarForegroundActive },
        }}
      >
        {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
      </IconButton>
    </Tooltip>
  );

  const navItems = [
    {
      key: "clients",
      label: "Clients",
      icon: <AccountCircleIcon fontSize="medium" />,
      path: "/clientDashboard",
      show: true,
      onClick: () =>
        navigate(
          {
            pathname: "/clientDashboard",
            search: `?${createSearchParams({ id })}`,
          }
        ),
    },
    {
      key: "admin",
      label: roles?.includes("ADMIN") ? "Admin" : "Management",
      icon: roles?.includes("ADMIN") ? (
        <AdminPanelSettingsIcon fontSize="medium" />
      ) : (
        <GroupsIcon fontSize="medium" />
      ),
      path: "/adminTable",
      show: roles?.includes("ADMIN") || roles?.includes("SALES_MANAGER"),
      onClick: () =>
        navigate(
          {
            pathname: "/adminTable",
            search: `?${createSearchParams({ adminId: id })}`,
          }
        ),
    },
    {
      key: "groups",
      label: "Groups",
      icon: <ApartmentIcon fontSize="medium" />,
      path: "/groupDashboard",
      show:
        roles?.includes("ADMIN") ||
        roles?.includes("GROUP_MANAGER") ||
        roles?.includes("GROUP_ADMIN"),
      onClick: () =>
        navigate(
          {
            pathname: "/groupDashboard",
            search: `?${createSearchParams({ adminId: id })}`,
          }
        ),
    },
    {
      key: "tutorials",
      label: "Tutorials",
      icon: <OndemandVideoIcon fontSize="medium" />,
      path: "/tutorials",
      show: true,
      onClick: () => navigate("/tutorials"),
    },
    {
      key: "settings",
      label: "Settings",
      icon: <SettingsIcon fontSize="medium" />,
      path: "/settings",
      show: true,
      onClick: () => navigate("/settings"),
    },
  ];

  const myName = [myFirstName, myLastName].filter(Boolean).join(" ");
  const initials = `${(myFirstName || "").charAt(0)}${(myLastName || "").charAt(0)}`.toUpperCase();

  const isActivePath = (path) => location.pathname.startsWith(path);

  const handleLogout = () => {
    resetUser();
    navigate("/");
  };

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      sx={{
        backgroundColor: "transparent",
        gap: 1,
        padding: 2,
      }}
    >
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        wrap="nowrap"
        sx={{ gap: 0.5 }}
      >
        {toggleButton}

        <Fade in={!collapsed} unmountOnExit timeout={250}>
          <Grid
            container
            direction="row"
            alignItems="center"
            wrap="nowrap"
            sx={{ gap: 0.5 }}
          >
            <Tooltip
              title={paramId ? `${fullName} Clients` : urlToPage[currentPage] || fullName}
            >
              <Chip
                label={paramId ? `${displayName} Clients` : urlToPage[currentPage] || displayName}
                sx={{
                  bgcolor: tokens.sidebarActive,
                  color: tokens.sidebarForegroundActive,
                  fontSize: "14px",
                  fontWeight: 600,
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: `1px solid ${tokens.sidebarHover}`,
                  maxWidth: 140,
                  minWidth: 0,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </Tooltip>

            <Tooltip title="Search (Ctrl/⌘ + K)">
              <IconButton
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
                sx={{ color: tokens.sidebarForegroundActive }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            <NotificationBell />
          </Grid>
        </Fade>
      </Grid>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      <Fade in={!collapsed} unmountOnExit timeout={250}>
        <Grid
          container
          direction="column"
          alignItems="center"
          sx={{ gap: 1.5, width: "100%" }}
        >
          <Divider sx={{ width: "80%", bgcolor: tokens.sidebarHover }} />

          {navItems
            .filter((item) => item.show)
            .map((item) => (
              <NavButton
                key={item.key}
                label={item.label}
                icon={item.icon}
                onClick={item.onClick}
                active={isActivePath(item.path)}
              />
            ))}

          <Divider sx={{ width: "80%", bgcolor: tokens.sidebarHover }} />

          {myName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "80%", px: 0.5 }}>
              <Avatar
                aria-hidden="true"
                sx={{ width: 32, height: 32, fontSize: 13, bgcolor: tokens.sidebarActive, color: tokens.sidebarForegroundActive }}
              >
                {initials}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: tokens.sidebarForegroundActive }}>
                  {myName}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, color: tokens.sidebarForeground }}>
                  {roleLabelFor(roles)}
                </Typography>
              </Box>
            </Box>
          )}

          <NavButton
            label="Logout"
            icon={<LogoutIcon fontSize="medium" />}
            onClick={handleLogout}
            sxOverride={{
              marginTop: "15px",
              color: tokens.sidebarDestructive,
              "&:hover": { bgcolor: tokens.sidebarHover, color: tokens.sidebarDestructiveHover },
            }}
          />
        </Grid>
      </Fade>

      <Fade in={collapsed} unmountOnExit timeout={250}>
        <Grid
          container
          direction="column"
          alignItems="center"
          sx={{ gap: 1, width: "100%" }}
        >
          <Divider sx={{ width: "60%", bgcolor: tokens.sidebarHover }} />

          {navItems
            .filter((item) => item.show)
            .map((item) => {
              const active = isActivePath(item.path);
              return (
                <Tooltip key={item.key} title={item.label} placement="right">
                  <IconButton
                    aria-label={item.label}
                    onClick={item.onClick}
                    aria-current={active ? "page" : undefined}
                    sx={{
                      color: tokens.sidebarForegroundActive,
                      bgcolor: active ? tokens.sidebarActive : "transparent",
                      "&:hover": { bgcolor: tokens.sidebarHover },
                    }}
                  >
                    {item.icon}
                  </IconButton>
                </Tooltip>
              );
            })}

          <Divider sx={{ width: "60%", bgcolor: tokens.sidebarHover }} />

          {myName && (
            <Tooltip title={`${myName} · ${roleLabelFor(roles)}`} placement="right">
              <Avatar
                sx={{ width: 32, height: 32, fontSize: 13, bgcolor: tokens.sidebarActive, color: tokens.sidebarForegroundActive }}
              >
                {initials}
              </Avatar>
            </Tooltip>
          )}

          <Tooltip title="Logout" placement="right">
            <IconButton
              aria-label="Logout"
              onClick={handleLogout}
              sx={{
                color: tokens.sidebarDestructive,
                "&:hover": { bgcolor: tokens.sidebarHover, color: tokens.sidebarDestructiveHover },
              }}
            >
              <LogoutIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Grid>
      </Fade>
    </Grid>
  );
}

function NavButton({ label, icon, onClick, sxOverride, active = false }) {
  const tokens = useTokens();
  return (
    <Button
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        width: "80%",
        color: active ? tokens.sidebarForegroundActive : tokens.sidebarForeground,
        fontSize: "15px",
        fontWeight: active ? 600 : 500,
        padding: "8px 12px",
        borderRadius: "8px",
        transition: "background-color 0.15s ease-in-out, color 0.15s ease-in-out",
        textTransform: "none",
        bgcolor: active ? tokens.sidebarActive : "transparent",
        boxShadow: active ? `inset 3px 0 0 ${tokens.sidebarForegroundActive}` : "none",
        "&:hover": {
          bgcolor: tokens.sidebarHover,
          color: tokens.sidebarForegroundActive,
        },
        ...sxOverride,
      }}
      startIcon={icon}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
    >
      {label}
    </Button>
  );
}
